import { Fragment, useMemo } from "react";
import { Alert, Button, Checkbox, Grid, Input } from "../../../components";
import { PriceSetting } from "../../programs/PriceSettingsInput";
import { formatMoney } from "../../../utils";
import { COMPANY_OPTIONS } from "../../../constants";
import { Add, Description } from "@mui/icons-material";
import { FormControlLabel, Link, Box } from "@mui/material";
import { FormikProps } from "formik";

const MANUAL_LINKS = {
  gol: {
    gmail: '/smiles-gmail.pdf',
    outlook: '/smiles-outlook.pdf'
  },  
  "gol_a_prazo": {
    gmail: '/smiles-gmail.pdf',
    outlook: '/smiles-outlook.pdf'
  },
  latam: {
    gmail: '/latam-gmail.pdf',
    outlook: '/latam-outlook.pdf'
  },
  qatar: {
    gmail: '/qatar-gmail.pdf',
    outlook: '/qatar-outlook.pdf'
  }
} as const;

export type Program = {
  company: string;
  active: boolean;
  needs_email_redirect?: boolean;
  custom_bid_disclaimer?: string;
  accepts_custom_payment: boolean;
  program_price_settings: { from: number; to?: number; price: number }[];
};

type NewBidFormStepOneProps = {
  formik: FormikProps<{
    company: string;
    miles: string;
    price: string;
    pax: string;
    account_id: string;
    accepts_custom_payment: boolean;
    needs_email_redirect: boolean;
  }>;
  programs: Program[];
  programPriceSettings: PriceSetting[];
  accountOptions: { label: string; value: string }[];
  createAccountModalVisibilityControls: {
    setTrue: () => void;
  };
};

const hasManualForCompany = (company: string): boolean => {
  const companyLower = company.toLowerCase();
  return Object.keys(MANUAL_LINKS).some(key => companyLower.includes(key));
};

const getManualCompanyKey = (company: string): keyof typeof MANUAL_LINKS | null => {
  const companyLower = company.toLowerCase();
  const key = Object.keys(MANUAL_LINKS).find(key => companyLower.includes(key));
  return key as keyof typeof MANUAL_LINKS || null;
};



export const NewBidFormStepOne = ({
  formik,
  programs,
  programPriceSettings,
  accountOptions,
  createAccountModalVisibilityControls,
}: NewBidFormStepOneProps) => {
  const selectedProgram = useMemo(() => {
    return (programs || []).find(
      (program: { company: string; active: boolean }) => {
        return program.company === formik.values.company;
      }
    );
  }, [formik.values.company, programs]);

  console.log(formik.values.company);
  

  return (
    <>
      {formik.values.company === "GOL" && (
        <Grid item xs={12}>
          <Alert icon={false} color="success">
            Em ofertas <strong>Smiles</strong> utilizamos preferencialmente
            nossos cartões <strong>Smiles Visa Infinite</strong>, que geram
            milesback após a data do voo.
          </Alert>
        </Grid>
      )}
      {programPriceSettings.length > 0 && (
        <Grid item xs={12}>
          <Alert icon={false} color="info">
            <strong>Valores Médios de Milheiro (cada mil milhas):</strong>
            {programPriceSettings.map((item: PriceSetting, index) => (
              <Fragment key={index}>
                <br />
                {item.to
                  ? `De ${Number(item.from).toLocaleString(
                      "pt-BR"
                    )} Até ${Number(item.to).toLocaleString(
                      "pt-BR"
                    )}: ${formatMoney(item.price)}`
                  : `A partir de ${Number(item.from).toLocaleString(
                      "pt-BR"
                    )}: ${formatMoney(item.price)}`}
              </Fragment>
            ))}
          </Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <Input
          label="Programa de Milhas"
          name="company"
          select
          options={COMPANY_OPTIONS.filter(({ value }) => {
            return (programs || []).find(
              (program: { company: string; active: boolean }) => {
                return program.company === value && program.active;
              }
            );
          })}
          onChange={(event) => {
            const selectedCompany = event.target.value;
            console.log("selectedCompany", selectedCompany);
            

            const relatedProgram = (programs || []).find(
              (program: { company: string; active: boolean }) =>
                program.company === selectedCompany && program.active
            );

            formik.setValues({
              ...formik.values,
              company: selectedCompany,
              price:
                relatedProgram &&
                relatedProgram.program_price_settings.length > 0
                  ? Number(
                      relatedProgram.program_price_settings[0].price
                    ).toFixed(2)
                  : ""
            });
          }}
          onBlur={formik.handleBlur}
          value={formik.values.company}
          helperText={formik.touched.company && formik.errors.company}
          error={!!formik.touched.company && !!formik.errors.company}
        />
      </Grid>
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={2} display={"flex"}>
            <Button
              onClick={createAccountModalVisibilityControls.setTrue}
              disabled={!formik.values.company}
            >
              <Add />
            </Button>
          </Grid>
          <Grid item xs={10}>
            <Input
              label="Conta do Programa de Milhas"
              name="account_id"
              select
              options={accountOptions}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.account_id}
              helperText={formik.touched.account_id && formik.errors.account_id}
              error={!!formik.touched.account_id && !!formik.errors.account_id}
              disabled={!formik.values.company}
            />
          </Grid>
        </Grid>
      </Grid>
      {selectedProgram && selectedProgram.needs_email_redirect && (
        <>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.needs_email_redirect || false}
                  onChange={() => {
                    formik.setFieldValue(
                      "needs_email_redirect",
                      !formik.values.needs_email_redirect,
                      true
                    );
                  }}
                />
              }
              label="Redirecionamento de e-mail"
            />
            {selectedProgram?.needs_email_redirect && hasManualForCompany(formik.values.company) && (
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link
                  href={MANUAL_LINKS[getManualCompanyKey(formik.values.company)!].gmail}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Description fontSize="small" />
                  Manual de Redirecionamento - Gmail
                </Link>
                <Link
                  href={MANUAL_LINKS[getManualCompanyKey(formik.values.company)!].outlook}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Description fontSize="small" />
                  Manual de Redirecionamento - Outlook
                </Link>
              </Box>
            )}
          </Grid>
          {!formik.values.needs_email_redirect && (
            <Grid item xs={12}>
              <Alert icon={false} color="error">
                <span>
                  O redirecionamento do e-mail deve ser confirmado para seguir
                  com a oferta dessa companhia.
                </span>
              </Alert>
            </Grid>
          )}
        </>
      )}
    </>
  );
};
